import { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';

/**
 * The whole application behind the user's Scheduler access.
 *
 * A user given no access to Scheduler gets nothing but the message below: the gate sits
 * above the sidebar, the pages and the providers that load their data, so no menu, button
 * or request of the application is ever mounted for them. The check is only made in live
 * mode, where the access comes from the platform; demo mode is ready straight away.
 */
export function SchedulerAccessGate({ children }: { children: ReactNode }) {
  const { bootstrapState } = useUser();

  // Nothing of the application is shown until the access is known, so a user without it
  // never sees the menus appear and disappear again.
  if (bootstrapState === 'loading')
    return <div className="fixed inset-0 bg-background" aria-busy="true" />;

  if (bootstrapState === 'no-access') return <NoSchedulerAccess />;

  return <>{children}</>;
}

function NoSchedulerAccess() {
  return (
    <div
      role="alert"
      className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-background p-6 text-center"
    >
      <Lock className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <h1 className="text-xl font-semibold text-foreground">Access denied</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        You do not have access to Scheduler. Please contact your administrator.
      </p>
    </div>
  );
}
